import { type FC, type MouseEventHandler, useState } from 'react';
import { Show } from '@components/Show';
import type { ResourceBlock } from '@domain/schemas/contract.schema';
import { cn } from 'cn';

import { useCommentMutations } from '../hooks/useCommentMutations';
import { scrollToThreadMark } from '../utils/scroll-to-thread-mark';
import { type CommentThread, getThreadReplyId } from '../utils/threads';

import { BlockComment } from './BlockComment';
import { CommentComposer } from './CommentComposer';
import { QuoteChip } from './QuoteChip';

interface CommentThreadViewProps {
  thread: CommentThread;
  block: ResourceBlock;
  isFocused: boolean;
  /** The stored anchor no longer matches the block content; shown as removed (spec §7.1 safety net). */
  isStale: boolean;
  canReply: boolean;
  canResolve: boolean;
  disableActions: boolean;
  onFocus: () => void;
}

export const CommentThreadView: FC<CommentThreadViewProps> = ({
  thread,
  block,
  isFocused,
  isStale,
  canReply,
  canResolve,
  disableActions,
  onFocus,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addComment, resolveConversation, unresolveConversation } = useCommentMutations();

  const { root, comments, isResolved, isAnchored, isDetached } = thread;
  const replyId = getThreadReplyId(thread);
  const isRemoved = isDetached || (isAnchored && isStale);
  // Legacy threadless roots cannot be resolved by thread id; the backend matches on thread_id only.
  const showResolveActions = canResolve && Boolean(root.thread_id);
  const showReply = isFocused && !isResolved && canReply;
  const isDisabled = disableActions || isSubmitting;

  const run = async (action: () => Promise<unknown>) => {
    setIsSubmitting(true);
    try {
      await action();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResolve: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    void run(() => resolveConversation.mutateAsync(replyId));
  };

  const handleUnresolve: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    void run(() => unresolveConversation.mutateAsync(replyId));
  };

  return (
    <div
      className={cn('relative flex w-full flex-col', isResolved && 'bg-gray-50')}
      data-testid={`thread-${thread.id}`}
      onClick={onFocus}
    >
      <Show when={isSubmitting}>
        <div className="bg-info-100 absolute inset-0 opacity-50" />
      </Show>

      <Show when={isAnchored || isDetached}>
        <QuoteChip
          isRemoved={isRemoved}
          onClick={isAnchored && !isRemoved ? () => scrollToThreadMark(thread.id) : undefined}
          quote={root.anchor?.quote ?? ''}
        />
      </Show>

      {comments.map((comment, idx) => (
        <BlockComment
          comment={comment}
          disableActions={isDisabled}
          idx={idx}
          isReviewStatus={showResolveActions}
          key={comment.id}
          onResolveConversation={handleResolve}
          onUnresolveConversation={handleUnresolve}
        />
      ))}

      <Show when={showReply}>
        <div className="mx-7 h-px bg-gray-200" />
        <CommentComposer
          autoFocus
          disabled={disableActions}
          onSubmit={(text) =>
            run(() => addComment.mutateAsync({ content: text, blockId: block.id, threadId: replyId }))
          }
          placeholder="Add a reply..."
          submitLabel="Reply"
        />
      </Show>
    </div>
  );
};
